// Signaling WS client
//
// Owns the `wss://.../v1/signal/{session_id}` socket: sends `auth`,
// awaits `auth_ack`, relays SDP + ICE candidates between the bootstrap
// and coordination. Coordination is a blind bridge; payloads are
// opaque to it.
//
// This module deliberately has no WebRTC knowledge — the caller
// provides a `SignalingHandlers` object with `onOfferPayload`,
// `onAnswerPayload`, `onCandidatePayload`. That keeps the WS client
// unit-testable with a fake socket and keeps RTC setup in
// connection.ts.

/** Every message carries `type` and `v`. Protocol version is pinned at 1. */
export const SIGNALING_PROTOCOL_VERSION = 1;

/** Relay payload shape */
export interface RelayPayload {
  kind: "offer" | "answer" | "candidate";
  /** For offer/answer. */
  sdp?: string;
  /** For candidate. */
  candidate?: {
    candidate: string;
    sdpMid: string | null;
    sdpMLineIndex: number | null;
  };
  /** REQUIRED on the box's initial offer/answer. 64-byte Ed25519 sig, base64. */
  dtls_fp_sig_b64?: string;
}

export type EndReason = "connected" | "aborted" | "timeout" | "error";

export interface SignalingHandlers {
  onAuthAck?: (heartbeatSec: number) => void;
  onRelayPayload: (payload: RelayPayload, seq: number) => void | Promise<void>;
  onEnd?: (reason: EndReason) => void;
  onError?: (code: string, message: string) => void;
}

/**
 * Minimal WebSocket-like interface. The real browser `WebSocket`
 * fulfils this; tests pass a fake. We only need string messages
 * (signaling is JSON text frames).
 */
export interface WebSocketLike {
  /** 0 connecting, 1 open, 2 closing, 3 closed (as on `WebSocket`). */
  readonly readyState?: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  addEventListener(type: "open", listener: () => void): void;
  addEventListener(type: "message", listener: (ev: { data: unknown }) => void): void;
  addEventListener(type: "close", listener: (ev: { code: number; reason: string }) => void): void;
  addEventListener(type: "error", listener: (ev: unknown) => void): void;
}

/** Factory so tests can inject a fake. Browser uses `new WebSocket(url)`. */
export type WebSocketFactory = (url: string) => WebSocketLike;

export interface SignalingClientOptions {
  signalUrl: string;
  signalToken: string;
  handlers: SignalingHandlers;
  /** Defaults to `new WebSocket(url)` when running in a browser. */
  wsFactory?: WebSocketFactory;
  /** Sent in `auth`; required when `preopened` targets `/v1/signal`. */
  sessionId?: string;
  /**
   * A socket opened ahead of `/v1/connect` against the session-less
   * `/v1/signal` route. Used instead of dialing `signalUrl` when it is
   * still connecting or open.
   */
  preopened?: WebSocketLike;
}

/**
 * State-machine client for the signaling WS. Call `open()` to start;
 * `sendRelay()` to forward an SDP answer or ICE candidate toward the
 * box; `end()` or `close()` to finish.
 *
 * The client keeps its own outbound `seq` counter per direction, as
 * defined in 
 */
export class SignalingClient {
  private ws: WebSocketLike | null = null;
  private outSeq = 0;
  private state: "idle" | "connecting" | "authed" | "closed" = "idle";
  /**
   * Tail-chained promise that serialises `relay` message processing.
   * `addEventListener` does not await async listeners, so two relay
   * frames that arrive back-to-back (the box's `offer` immediately
   * followed by its first `candidate`) would otherwise race: the
   * candidate handler enters `pc.addIceCandidate()` while the offer
   * handler is still mid-`pc.setRemoteDescription()` and throws
   * "remote description was null". The box sends frames in the order
   * defined by ; the client just has to honour
   * that ordering at the handler layer.
   */
  private relayChain: Promise<void> = Promise.resolve();

  constructor(private readonly opts: SignalingClientOptions) {}

  /** Open the socket, send `auth`, and start listening. Resolves on auth_ack.
   *
   *  If `signal` is provided and aborts before `auth_ack` arrives, the
   *  promise rejects with `signaling: aborted` and the WS is closed.
   *  Callers (the reconnect loop) rely on this so a stalled WS
   *  handshake doesn't wedge the whole session. */
  open(signal?: AbortSignal): Promise<void> {
    if (this.state !== "idle") {
      return Promise.reject(new Error("signaling: open() called twice"));
    }
    if (signal?.aborted) {
      return Promise.reject(new Error("signaling: aborted"));
    }
    this.state = "connecting";

    const factory = this.opts.wsFactory ?? defaultWsFactory;
    const early = this.opts.preopened;
    const earlyUsable = early !== undefined && (early.readyState ?? 0) <= 1;
    if (early && !earlyUsable) {
      try {
        early.close();
      } catch {
        /* ignore */
      }
    }
    const ws = earlyUsable ? early : factory(this.opts.signalUrl);
    this.ws = ws;

    return new Promise<void>((resolve, reject) => {
      let authResolved = false;
      const onAbort = (): void => {
        if (authResolved) return;
        authResolved = true;
        try {
          ws.close();
        } catch {
          /* ignore */
        }
        this.state = "closed";
        reject(new Error("signaling: aborted"));
      };
      signal?.addEventListener("abort", onAbort);

      const sendAuth = (): void => {
        ws.send(
          JSON.stringify({
            type: "auth",
            v: SIGNALING_PROTOCOL_VERSION,
            signal_token: this.opts.signalToken,
            ...(this.opts.sessionId ? { session_id: this.opts.sessionId } : {}),
          }),
        );
      };
      if (ws.readyState === 1) sendAuth();
      else ws.addEventListener("open", sendAuth);

      ws.addEventListener("message", (ev) => {
        let msg: SignalingMessage;
        try {
          msg = parseMessage(ev.data);
        } catch (e) {
          this.opts.handlers.onError?.("malformed_message", (e as Error).message);
          return;
        }
        switch (msg.type) {
          case "auth_ack":
            this.state = "authed";
            if (!authResolved) {
              authResolved = true;
              this.opts.handlers.onAuthAck?.(msg.heartbeat_interval_s);
              resolve();
            }
            break;
          case "relay": {
            // Tail-chain onto `relayChain` so each relay handler fully
            // resolves before the next one starts — see the field
            // docstring. `auth_ack` / `end` / `error` don't need this;
            // they just flip state synchronously.
            const relayMsg = msg;
            this.relayChain = this.relayChain.then(async () => {
              try {
                const payload = decodeRelayPayload(relayMsg.payload_b64);
                await this.opts.handlers.onRelayPayload(payload, relayMsg.seq);
              } catch (e) {
                this.opts.handlers.onError?.(
                  "malformed_relay",
                  (e as Error).message,
                );
              }
            });
            break;
          }
          case "end":
            this.opts.handlers.onEnd?.(msg.reason);
            break;
          case "error":
            this.opts.handlers.onError?.(msg.code, msg.message);
            break;
        }
      });

      ws.addEventListener("close", (ev) => {
        this.state = "closed";
        if (!authResolved) {
          authResolved = true;
          reject(
            new Error(
              `signaling: WS closed before auth_ack (code=${ev.code}, reason=${JSON.stringify(ev.reason)})`,
            ),
          );
        }
      });

      ws.addEventListener("error", () => {
        if (!authResolved) {
          authResolved = true;
          reject(new Error("signaling: WS error before auth_ack"));
        }
      });
    });
  }

  /**
   * Send a relay payload toward the box. Base64-encodes the inner
   * JSON and bumps the outbound seq counter.
   */
  sendRelay(payload: RelayPayload): void {
    if (!this.ws || this.state === "idle" || this.state === "closed") {
      throw new Error("signaling: sendRelay() before auth or after close");
    }
    const seq = this.outSeq++;
    const envelope = {
      type: "relay",
      v: SIGNALING_PROTOCOL_VERSION,
      seq,
      payload_b64: encodeRelayPayload(payload),
    };
    this.ws.send(JSON.stringify(envelope));
  }

  /** Send an `end` message. Does not close the socket — coord closes after mirroring. */
  end(reason: EndReason): void {
    if (!this.ws) return;
    this.ws.send(
      JSON.stringify({ type: "end", v: SIGNALING_PROTOCOL_VERSION, reason }),
    );
  }

  /** Close the underlying socket. */
  close(code = 1000, reason = "done"): void {
    if (this.ws) this.ws.close(code, reason);
    this.state = "closed";
  }

  get currentOutSeq(): number {
    return this.outSeq;
  }
}

// ---------- message parsing ----------

type SignalingMessage =
  | { type: "auth_ack"; heartbeat_interval_s: number }
  | { type: "relay"; seq: number; payload_b64: string }
  | { type: "end"; reason: EndReason }
  | { type: "error"; code: string; message: string };

function parseMessage(data: unknown): SignalingMessage {
  if (typeof data !== "string") {
    throw new Error("expected text frame");
  }
  const obj = JSON.parse(data) as Record<string, unknown>;
  const type = obj["type"];
  switch (type) {
    case "auth_ack":
      return {
        type: "auth_ack",
        heartbeat_interval_s: Number(obj["heartbeat_interval_s"] ?? 15),
      };
    case "relay": {
      const seq = Number(obj["seq"]);
      const payloadB64 = obj["payload_b64"];
      if (!Number.isFinite(seq) || typeof payloadB64 !== "string") {
        throw new Error("relay: missing seq/payload_b64");
      }
      return { type: "relay", seq, payload_b64: payloadB64 };
    }
    case "end": {
      const reason = String(obj["reason"] ?? "error") as EndReason;
      return { type: "end", reason };
    }
    case "error":
      return {
        type: "error",
        code: String(obj["code"] ?? "unknown"),
        message: String(obj["message"] ?? ""),
      };
    default:
      throw new Error(`unknown signaling message type: ${String(type)}`);
  }
}

// ---------- payload base64 codec ----------

export function encodeRelayPayload(payload: RelayPayload): string {
  const json = JSON.stringify(payload);
  return base64Encode(new TextEncoder().encode(json));
}

export function decodeRelayPayload(b64: string): RelayPayload {
  const bytes = base64Decode(b64);
  const json = new TextDecoder().decode(bytes);
  return JSON.parse(json) as RelayPayload;
}

export function base64Encode(bytes: Uint8Array): string {
  // btoa is available in browsers and in Node 18+.
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

export function base64Decode(b64: string): Uint8Array {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

// ---------- default WS factory ----------

const defaultWsFactory: WebSocketFactory = (url) => {
  // The browser `WebSocket` satisfies WebSocketLike natively.
  const ctor = (globalThis as { WebSocket?: new (url: string) => WebSocketLike }).WebSocket;
  if (!ctor) {
    throw new Error(
      "no WebSocket constructor available (are you in a browser?)",
    );
  }
  return new ctor(url);
};
